"use client"

import { useAudioPlayer } from "./audio-player-provider"
import { Button } from "./ui/button"
import { X, Play, Pause, SkipBack, SkipForward, Loader2, Volume2, VolumeX, Download, ExternalLink } from "lucide-react"

const formatTime = (time: number) => {
  if (!Number.isFinite(time)) return "0:00"
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

export function GlobalAudioModal() {
  const {
    currentTrack,
    isExpanded,
    closeExpanded,
    currentTime,
    duration,
    togglePlayPause,
    isPlaying,
    seek,
    stop,
    seekBy,
    isBuffering,
    volume,
    setVolume,
    isMuted,
    toggleMute,
    playbackRate,
    setPlaybackRate,
  } = useAudioPlayer()

  if (!currentTrack || !isExpanded) {
    return null
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur" onClick={closeExpanded}>
      <div
        className="w-[92vw] max-w-2xl rounded-2xl bg-background p-6 shadow-2xl border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Now Listening</p>
            <h2 className="text-2xl font-semibold">{currentTrack.title}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={closeExpanded}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-6">
          <div
            className="h-2 rounded-full bg-muted cursor-pointer relative"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect()
              const percent = (event.clientX - rect.left) / rect.width
              seek(percent * duration)
            }}
          >
            <div className="absolute inset-y-0 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-2 flex justify-between text-xs text-muted-foreground font-mono">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          <div className="flex items-center justify-center gap-4">
            <Button 
              variant="ghost" 
              className="h-12 px-4 flex flex-col items-center gap-1" 
              onClick={() => seekBy(-10)} 
              disabled={isBuffering}
              title="Skip back 10 seconds"
            >
              <SkipBack className="h-5 w-5" />
              <span className="text-xs text-muted-foreground">10s</span>
            </Button>
            <Button size="lg" className="rounded-full h-16 w-16" onClick={togglePlayPause} disabled={isBuffering}>
              {isBuffering ? <Loader2 className="h-6 w-6 animate-spin" /> : isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-1" />}
            </Button>
            <Button 
              variant="ghost" 
              className="h-12 px-4 flex flex-col items-center gap-1" 
              onClick={() => seekBy(10)} 
              disabled={isBuffering}
              title="Skip forward 10 seconds"
            >
              <SkipForward className="h-5 w-5" />
              <span className="text-xs text-muted-foreground">10s</span>
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-muted-foreground">Speed</span>
              {[0.75, 1, 1.25, 1.5].map((rate) => (
                <Button
                  key={rate}
                  size="sm"
                  variant={playbackRate === rate ? "default" : "outline"}
                  onClick={() => setPlaybackRate(rate)}
                >
                  {rate}x
                </Button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-1 min-w-[180px]">
              <Button variant="outline" size="icon" onClick={toggleMute}>
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : volume}
                onChange={(event) => setVolume(parseFloat(event.target.value))}
                className="flex-1"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={stop}>
              Stop
            </Button>
            <Button variant="ghost" onClick={() => window.open(currentTrack.url, "_blank")}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Open in new tab
            </Button>
            <Button variant="ghost" onClick={() => {
              const link = document.createElement("a")
              link.href = currentTrack.url
              link.download = `${currentTrack.title}.mp3`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

